import React, { createRef, useEffect, useState } from "react";
import { StyleSheet, View, SafeAreaView, ScrollView, Linking } from "react-native";
import { HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { getDisplayableLocalTime } from "../../utils/date";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers";
import { CheckType, useLocalMock } from "../../enums/check";
import {
  setCheckRequestIsLoading,
  setCheckScanPaymentConfirmed,
  setDuplicateOrderId,
} from "../../redux/actions/actionCreators";
import { DomusoApiService } from "../../api/DomusoApiService";
import { MockService } from "../../api/MockService";
import { uuidv4 } from "../../utils/uuidv4";
import {
  ocrAmount,
  ocrAmountConfidence,
  payerName,
  payerNameConfidence,
} from "../../utils/recognition";
import { deriveUnitTitle } from "../../utils";
import { shortDate } from "../../utils/date";
import { useNavigation } from "@react-navigation/native";
import { UserService } from "../../api/UserService";
import { CheckboxStatusType } from "../../../types";
import {
  typography,
  theme as themeUiComponent,
  fontWeights,
  Icon,
  Checkbox as CheckboxUiComponent,
  ErrorWarningBottomSheet,
  Button,
  Text,
} from "ui-components";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { Grid, Container, Row, Column } from "ui-grid";
import { PaymentFlowMobileCheckPayProgressIndicator } from "../../components/new/payment-flow/payment-flow-mobile-check-pay-progress-indicator";
import { PaymentHeading as PaymentFlowPaymentHeading } from "../../components/new/payment-flow/payment-flow-heading";
import { translate } from "../../i18n/translate";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useConfig } from "../../hooks/useConfig";
import { BottomNav } from "../../components/layout/bottom-nav";
import { Footer } from "../../components/new/footer/footer";
import { reviewMobileCheckPaySchema } from "./utils/validation-schemas-payment";
import { Spacer } from "../../components/spacer/spacer";
import { toHumanDollars } from "../../utils/money";
import { useDetermineChannel } from "../../hooks/useDetermineChannel";

interface CheckInfo {
  checkNumber: string;
  checkType: string;
  routingNumber: string;
  accountNumber: string;
}

const PaymentFlowPaymentMethodMobileCheckPaymentSummary = ({ route }: HomeScreenProps) => {
  const { params } = route;

  const {
    propName,
    propertyUnit,
    reservationId,
    currentProperty,
    checkTypeProp,
    paymentScheduleType,
    paymentDate,
  } = params;
  const { id: propId } = currentProperty;
  const date = getDisplayableLocalTime(new Date()).format("MMMM DD, YYYY");
  const checkData = useSelector((state: RootState) => state.check.checkData);
  const isLoading = useSelector((state: RootState) => state.check.isLoading);
  const front = useSelector((state: RootState) => state.check.frontResponse);
  const back = useSelector((state: RootState) => state.check.backResponse);
  const styles = getStyles();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const logger = useSelector((state: RootState) => state.logger);
  const config = useConfig();
  const refPopupWarning = createRef();

  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const userService = new UserService(cognitoUser, dispatch);
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [confirmCheckDetailsMatch, setCancellationRefund] =
    useState<CheckboxStatusType>("unchecked");
  const [returnWarning, setReturnWarning] = useState<CheckboxStatusType>("unchecked");
  const [cof, setCof] = useState<CheckboxStatusType>("checked");

  const [channel] = useDetermineChannel();
  const [cancellationRefundCheck, setCancellationRefundCheck] = useState<boolean>(false);
  const [returnWarningCheck, setReturnWarningCheck] = useState<boolean>(false);
  let defaultValues: any = {
    cof,
    confirmCheckDetailsMatch,
    returnWarning,
  };

  const domusoApiService = useLocalMock
    ? new MockService(cognitoUser, dispatch)
    : new DomusoApiService(cognitoUser, dispatch);

  const getCheckTypeName = () => {
    return checkData.checkType === CheckType.CERTIFIED
      ? translate("paymentFlow.paymentMethodMobileCheckPay.summary.certifiedCheck")
      : translate("paymentFlow.paymentMethodMobileCheckPay.summary.personalCheck");
  };

  const navigateToCheckScanConfirmation = () => {
    return navigation.navigate("PaymentFlowPaymentTrasmitted", {
      fromMobileCheckPay: true,
      ...params,
    });
  };
  const paymentSchema = reviewMobileCheckPaySchema;

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(paymentSchema),
    defaultValues,
  });
  const isContinueDisabled =
    isLoading || (returnWarningCheck && cancellationRefundCheck) ? false : true;
  const preSelectedPaymentSourceId = useSelector((state) => state.payment.paymentSourceId);

  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);
  const getFeeForPaymentMethods = (platformFee: any) => {
    return platformFee ? `${toHumanDollars(platformFee)}` : `$0.00`;
  };
  const checkScanCertifiedFee = getFeeForPaymentMethods(propertyFees?.checkScanCertified?.fee);
  const checkScanPersonalFee = getFeeForPaymentMethods(propertyFees?.checkScanPersonal?.fee);

  useEffect(() => {
    register("cof");
    register("confirmCheckDetailsMatch");
    register("returnWarning");
  }, [register]);

  const checkDuplicatesAndMakeAPayment = async () => {
    try {
      dispatch(setCheckRequestIsLoading(true));
      const checkInfo = {
        checkNumber: checkData.checkNumber,
        checkType: checkData.checkType.toUpperCase(),
        routingNumber: checkData.routingNumber,
        accountNumber: checkData.accountNumber,
      };

      await checkDuplicates(checkInfo);
      await makeAPayment(checkInfo);

      dispatch(setCheckRequestIsLoading(false));
      dispatch(setCheckScanPaymentConfirmed(true));
      navigateToCheckScanConfirmation();

      logger.infoObject({
        reservationId: reservationId,
        paymentScheduleType: paymentScheduleType,
        message: "Create payment succeeded",
        paymentSourceType: `CHECK_${checkData?.checkType}`,
      });
    } catch (e) {
      dispatch(setCheckScanPaymentConfirmed(false));
      navigateToCheckScanConfirmation();
      const errorData = e?.error?.response?.data;
      logger.error({
        exception: errorData ? errorData : e,
        reservationId: reservationId,
        paymentScheduleType: paymentScheduleType,
        message: `Create payment error`,
        paymentSourceType: `CHECK_${checkData?.checkType}`,
      });
    }
  };

  const checkDuplicates = async (checkInfo: CheckInfo) => {
    const findDuplicateResponse = await domusoApiService.findDuplicates(
      checkInfo.checkNumber,
      checkInfo.checkType,
      checkInfo.accountNumber,
      checkInfo.routingNumber
    );

    if (findDuplicateResponse.count > 0) {
      const dupeOrderId = shortDate(findDuplicateResponse.orderId);
      dispatch(setDuplicateOrderId(dupeOrderId));
      throw new Error("duplicate found");
    }
  };
  const makeAPayment = async (checkInfo: CheckInfo) => {
    const request = {
      request_id: uuidv4(),
      approved: true, // not to be confused with paymentTransaction approved status
      date_received: new Date().toISOString(),
      routing_number: checkInfo.routingNumber,
      account_number: checkInfo.accountNumber,
      check_number: checkInfo.checkNumber,
      check_type: checkInfo.checkType,
      amount: parseInt((checkData.amount * 100).toFixed(0)),
      ocr_amount:
        front && front.recognition_result
          ? parseInt((ocrAmount(front.recognition_result) * 100).toFixed(0))
          : 0,
      ocr_amount_confidence:
        front && front.recognition_result ? ocrAmountConfidence(front.recognition_result) : 0,
      ocr_payer_name: front && front.recognition_result ? payerName(front.recognition_result) : "",
      ocr_payer_name_confidence:
        front && front.recognition_result ? payerNameConfidence(front.recognition_result) : 0,
      front_orig_url: front ? front.orig_image_url : "",
      front_tiff_url: front ? front.raw_image_url : "",
      front_report_tiff_url: front ? front.report_image_url : "",
      back_tiff_url: back ? back.raw_image_url : "",
      back_report_tiff_url: back ? back.report_image_url : "",
      micr: front ? front.cropped_micr : "",
      reservation_id: reservationId,
      prop_id: propId,
    };
    const response = await domusoApiService.submitCheck(request);
    const { payment_schedule_id } = response;

    userService.sendFootprints({
      payment_schedule_id,
      username: userInfo.username,
      interaction_type: "PAYMENT_SCHEDULE",
      channel,
    });
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
        <View style={{ marginVertical: 20 }}>
          <PaymentFlowMobileCheckPayProgressIndicator currentPosition={1} />
        </View>
        <PaymentFlowPaymentHeading
          heading={translate("paymentFlow.paymentMethodMobileCheckPay.summary.heading")}
        />
        <Grid>
          <Container>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.rowTitle}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .userProperty}>{`${propName}`}</Text>
                  <Text
                    style={styles.rowTitleRight}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .userPropertyUnit}>{`${deriveUnitTitle(propertyUnit)}`}</Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <RenderSeparator />
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .paymentDateText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.paymentDate")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .paymentDate}>
                    {date}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .paymentMethodText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.paymentMethod")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .paymentMethodInfo}>{`${getCheckTypeName()}`}</Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .checkNumberText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.checkNumber")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .checkNumber}>
                    {checkData.checkNumber}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .routingNumberText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.routingNumber")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .routingNumber}>
                    {checkData.routingNumber}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .accountNumberText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.accountNumber")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .accountNumber}>
                    {checkData.accountNumber}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .amountText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.amount")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .amount}>
                    {`${toHumanDollars(parseInt((checkData.amount * 100).toFixed(0)))}`}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.paymentDetailsLeftText}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .platformFeeText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.platformFee")}
                  </Text>
                  <Text
                    style={[styles.paymentDetailsRightText]}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .platformFee}>
                    {checkTypeProp == CheckType.PERSONAL
                      ? checkScanPersonalFee
                      : checkScanCertifiedFee}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <RenderSeparator />
              </Column>
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={{ margin: 6 }} />
                <View style={styles.inputRowInner}>
                  <Text
                    style={styles.rowTitle}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .totalText}>
                    {translate("paymentFlow.paymentMethodMobileCheckPay.summary.total")}
                  </Text>
                  <Text
                    style={styles.rowTitleRight}
                    {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                      .total}>
                    {`${toHumanDollars(parseInt((checkData.amount * 100).toFixed(0)))}`}
                  </Text>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>

            {errors.confirmCheckDetailsMatch ? (
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ margin: 8 }} />
                  <View style={styles.inputRowInner}>
                    <View style={styles.errorCancellationAndRefund}>
                      <View style={{ marginTop: 2 }}>
                        <Icon
                          color={themeUiComponent.colors.alert}
                          size={themeUiComponent.iconSize.small}
                          name="error"
                        />
                      </View>
                      <Text
                        style={styles.alertText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                          .errorCheckDetailsCheckboxNotChecked}>
                        {translate(
                          "paymentFlow.paymentMethodMobileCheckPay.summary.errorCheckDetailsCheckbox"
                        )}
                      </Text>
                    </View>
                  </View>
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            ) : null}

            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                {!errors.confirmCheckDetailsMatch && <Spacer size={"medium"} />}

                <View style={styles.inputRowInner}>
                  <View style={{ flexDirection: "row", flex: 1 }}>
                    <View style={{ paddingTop: 3 }}>
                      <CheckboxUiComponent
                        disabled={isLoading}
                        style={styles.checkboxStyle}
                        onValueChange={(newValue: boolean) => {
                          setValue("confirmCheckDetailsMatch", newValue ? "checked" : "unchecked");
                          setCancellationRefundCheck(newValue);
                          if (newValue) {
                            trigger();
                          }
                        }}
                        value={cancellationRefundCheck}
                        {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                          .checkDetailsCheckbox}
                      />
                    </View>
                    <Text
                      style={styles.policyText}
                      {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                        .checkDetailsText}>
                      <Text>{`${translate(
                        "paymentFlow.paymentMethodMobileCheckPay.summary.confirmCheckNormal1"
                      )}`}</Text>
                      <Text style={styles.checkboxLabelBold}>
                        {`${translate(
                          "paymentFlow.paymentMethodMobileCheckPay.summary.confirmCheckBold1"
                        )}`}
                      </Text>
                      <Text>{`${translate(
                        "paymentFlow.paymentMethodMobileCheckPay.summary.confirmCheckNormal2"
                      )}`}</Text>
                      <Text style={styles.checkboxLabelBold}>
                        {`${translate(
                          "paymentFlow.paymentMethodMobileCheckPay.summary.confirmCheckBold2"
                        )}`}
                      </Text>
                      <Text>{`${translate(
                        "paymentFlow.paymentMethodMobileCheckPay.summary.confirmCheckNormal3"
                      )}.`}</Text>
                    </Text>
                  </View>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>

            {errors.returnWarning ? (
              <Row>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                  <View style={{ margin: 8 }} />
                  <View style={styles.inputRowInner}>
                    <View style={styles.errorCancellationAndRefund}>
                      <View style={{ marginTop: 2 }}>
                        <Icon
                          color={themeUiComponent.colors.alert}
                          size={themeUiComponent.iconSize.small}
                          name="error"
                        />
                      </View>
                      <Text
                        style={styles.alertText}
                        {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                          .errorCheckDetailsCheckboxNotChecked}>
                        {translate(
                          "paymentFlow.paymentMethodMobileCheckPay.summary.errorMissingAcceptanceCheckbox"
                        )}
                      </Text>
                    </View>
                  </View>
                  <View style={{ margin: 8 }} />
                </Column>
                <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              </Row>
            ) : null}

            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                {!errors.returnWarning && <Spacer size={"medium"} />}
                <View style={styles.inputRowInner}>
                  <View style={{ flexDirection: "row", flex: 1 }}>
                    <View style={{ paddingTop: 3 }}>
                      <CheckboxUiComponent
                        disabled={isLoading}
                        style={styles.checkboxStyle}
                        onValueChange={(newValue: boolean) => {
                          setValue("returnWarning", newValue ? "checked" : "unchecked");
                          setReturnWarningCheck(newValue);
                          if (newValue) {
                            trigger();
                          }
                        }}
                        value={returnWarningCheck}
                        {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                          .returnWarningCheckbox}
                      />
                    </View>
                    <Text
                      style={styles.policyText}
                      {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                        .returnWarningText}>
                      {`${translate(
                        "paymentFlow.paymentMethodMobileCheckPay.summary.returnWarningMessage"
                      )}`}
                    </Text>
                  </View>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          </Container>
        </Grid>
      </ScrollView>

      <BottomNav backgroundColor={themeUiComponent.colors.white}>
        <View style={{ marginTop: 25 }}></View>
        <Row>
          <Column span={{ xs: false, sm: 1, md: 2, lg: 3 }} />
          <Column span={{ xs: 12, sm: 10, md: 8, lg: 6 }}>
            <Button
              text={translate("paymentFlow.paymentMethodMobileCheckPay.summary.cancel") as string}
              onPress={() => {
                refPopupWarning?.current?.open();
              }}
              type={"secondary"}
              {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                .cancelButton}
            />
            <View style={{ paddingVertical: 5 }}></View>
            <View style={{ paddingVertical: 5 }}>
              <Button
                type={"primary"}
                text={
                  translate(
                    "paymentFlow.paymentMethodMobileCheckPay.summary.submitPayment"
                  ) as string
                }
                loading={isLoading}
                disabled={isLoading}
                style={{
                  backgroundColor:
                    isLoading || isContinueDisabled
                      ? themeUiComponent.colors.text2
                      : themeUiComponent.colors.primary,
                }}
                onPress={handleSubmit(checkDuplicatesAndMakeAPayment)}
                // onPress={handleSubmit(onSubmit)}
                {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
                  .submitPaymentButton}
              />
            </View>
          </Column>
          <Column span={{ xs: false, sm: 1, md: 2, lg: 3 }} />
        </Row>
      </BottomNav>
      <Footer />

      <ErrorWarningBottomSheet
        ref={refPopupWarning}
        height={190}
        title={
          translate(
            "paymentFlow.paymentMethodMobileCheckPay.summary.cancelPaymentWarningTitle"
          ) as string
        }
        body={
          translate(
            "paymentFlow.paymentMethodMobileCheckPay.summary.cancelPaymentWarningSubTitle"
          ) as string
        }
        alertType={"warning"}
        leftButtonType={"secondary"}
        leftButtonText={
          translate(
            "paymentFlow.paymentMethodMobileCheckPay.summary.cancelPaymentWarningCancelButtonText"
          ) as string
        }
        rightButtonText={
          translate(
            "paymentFlow.paymentMethodMobileCheckPay.summary.cancelPaymentWarningStayButtonText"
          ) as string
        }
        onPressLeftButton={() => {
          navigation.navigate("Home");
          refPopupWarning?.current?.close();
        }}
        onPressRightButton={() => refPopupWarning?.current?.close()}
        {...automationTestingLocators.paymentFlowPaymentMethodMobileCheckPaySummary
          .cancelPaymentWarningPopup}
      />
    </SafeAreaView>
  );
};
const RenderSeparator = () => {
  return <View style={getStyles().separator} />;
};
const getStyles = () => {
  return StyleSheet.create({
    container: {
      flex: 1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: "#FFFFFF",
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: "#FFFFFF",
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: "#FFFFFF",
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
    },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      }),
    },
    scrollView: {
      flexGrow: 0,
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 40,
          paddingRight: 40,

          backgroundColor: "white",

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
      }),
    },
    inputRowInner: {
      flexDirection: "row",
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 30,
          paddingRight: 30,
        },
      }),
    },
    separator: {
      borderColor: themeUiComponent.colors.stroke,
      borderTopWidth: 0.7,
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          marginLeft: 26,
          marginRight: 26,
        },
        webDesktop: {
          marginLeft: 26,
          marginRight: 26,
        },
        iosPhone: {},
        iosTablet: {
          marginLeft: 26,
          marginRight: 26,
        },
        androidPhone: {},
        androidTablet: {
          marginLeft: 26,
          marginRight: 26,
        },
      }),
    },
    paymentDetailsLeftText: {
      ...typography.title2,
      color: themeUiComponent.colors.text3,
      fontWeight: fontWeights.Regular,
      flex: 1,
    },
    paymentDetailsRightText: {
      ...typography.title2,
      color: themeUiComponent.colors.text3,
      fontWeight: fontWeights.Bold,
      fontFamily: "Lato_700Bold",
      textAlign: "right",
      marginLeft: 10,
    },
    rowTitle: {
      ...typography.title2,
      fontFamily: "Lato_700Bold",
      color: themeUiComponent.colors.text4,
      flex: 1,
    },
    rowTitleRight: {
      ...typography.title2,
      color: themeUiComponent.colors.text4,
      fontFamily: "Lato_700Bold",
    },
    policyText: {
      ...typography.title2,
      color: themeUiComponent.colors.text3,
      marginLeft: 10,
      flex: 1,
    },
    alertText: {
      ...typography.body,
      color: themeUiComponent.colors.alert,
      marginLeft: 10,
      flex: 1,
    },
    checkboxStyle: {
      width: 20,
      height: 20,
    },
    errorCancellationAndRefund: {
      flexDirection: "row",
      flex: 1,
    },
    checkboxLabelBold: {
      fontFamily: "Lato_700Bold",
    },
  });
};
export default PaymentFlowPaymentMethodMobileCheckPaymentSummary;
